import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { publicRoutes } from "app/router/public/routes";
import {
  COOKIE_PREFERENCES_STORAGE_KEY,
  OPEN_COOKIE_PREFERENCES_EVENT,
} from "shared/feedback/cookiePreferences";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  personalization: boolean;
  marketing: boolean;
  updatedAt: string;
}

type EditablePreference = "analytics" | "personalization" | "marketing";

const defaultCookiePreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  personalization: false,
  marketing: false,
  updatedAt: "",
};

const cookiePolicyHref = `${publicRoutes["privacidade-lgpd"].path}#politica-cookies`;

function normalizeCookiePreferences(
  rawPreferences?: Partial<CookiePreferences>,
): CookiePreferences {
  return {
    essential: true,
    analytics: Boolean(rawPreferences?.analytics),
    personalization: Boolean(rawPreferences?.personalization),
    marketing: Boolean(rawPreferences?.marketing),
    updatedAt: rawPreferences?.updatedAt ?? "",
  };
}

function readStoredCookiePreferences(): CookiePreferences | null {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.localStorage.getItem(COOKIE_PREFERENCES_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    return normalizeCookiePreferences(JSON.parse(rawValue));
  } catch {
    return null;
  }
}

export function CookieConsentBanner() {
  const [isOpen, setIsOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [hasStoredPreferences, setHasStoredPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(
    defaultCookiePreferences,
  );

  useEffect(() => {
    const storedPreferences = readStoredCookiePreferences();

    if (storedPreferences) {
      setPreferences(storedPreferences);
      setHasStoredPreferences(true);
      return;
    }

    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleOpenPreferences = () => {
      const storedPreferences =
        readStoredCookiePreferences() ?? defaultCookiePreferences;

      setPreferences(storedPreferences);
      setHasStoredPreferences(Boolean(readStoredCookiePreferences()));
      setShowPreferences(true);
      setIsOpen(true);
    };

    window.addEventListener(
      OPEN_COOKIE_PREFERENCES_EVENT,
      handleOpenPreferences,
    );

    return () => {
      window.removeEventListener(
        OPEN_COOKIE_PREFERENCES_EVENT,
        handleOpenPreferences,
      );
    };
  }, []);

  const preferenceOptions = useMemo(
    () => [
      {
        key: "analytics" as const,
        title: "Cookies analíticos",
        description:
          "Ajudam a entender navegação, desempenho e pontos de melhoria da experiência.",
      },
      {
        key: "personalization" as const,
        title: "Cookies de preferência",
        description:
          "Memorizam idioma, escolhas visuais e ajustes do ambiente institucional.",
      },
      {
        key: "marketing" as const,
        title: "Cookies de comunicação",
        description:
          "Permitem personalizar campanhas e mensurar a efetividade de conteúdos institucionais.",
      },
    ],
    [],
  );

  const persistPreferences = (
    nextPreferences: Omit<CookiePreferences, "updatedAt">,
  ) => {
    const normalizedPreferences = normalizeCookiePreferences({
      ...nextPreferences,
      updatedAt: new Date().toISOString(),
    });

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        COOKIE_PREFERENCES_STORAGE_KEY,
        JSON.stringify(normalizedPreferences),
      );
    }

    setPreferences(normalizedPreferences);
    setHasStoredPreferences(true);
    setShowPreferences(false);
    setIsOpen(false);
  };

  const handleTogglePreference = (preference: EditablePreference) => {
    setPreferences((currentPreferences) => ({
      ...currentPreferences,
      [preference]: !currentPreferences[preference],
    }));
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1400,
        p: { xs: 1.5, lg: 2 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            borderRadius: 3,
            border: "1px solid rgba(15, 23, 42, 0.12)",
            bgcolor: "rgba(255, 255, 255, 0.96)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.18)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1.2fr auto" },
              gap: 3,
              p: { xs: 2.4, lg: 3 },
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: "1.1rem", lg: "1.2rem" },
                  color: "var(--oxy-text-primary)",
                  mb: 0.8,
                }}
              >
                Preferências de cookies
              </Typography>
              <Typography sx={{ color: "var(--oxy-text-strong)", lineHeight: 1.7 }}>
                Utilizamos cookies essenciais para o funcionamento do portal e,
                mediante consentimento, cookies adicionais para análise,
                preferências e comunicação institucional.
              </Typography>
              <Button
                component="a"
                href={cookiePolicyHref}
                variant="text"
                sx={policyLinkButtonSx}
              >
                Ler política de cookies
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "wrap",
                gap: 1.2,
                justifyContent: { lg: "flex-end" },
              }}
            >
              <Button
                onClick={() => setShowPreferences((currentState) => !currentState)}
                variant="outlined"
                sx={secondaryButtonSx}
              >
                {showPreferences ? "Ocultar opções" : "Gerenciar cookies"}
              </Button>
              <Button
                onClick={() =>
                  persistPreferences({
                    essential: true,
                    analytics: false,
                    personalization: false,
                    marketing: false,
                  })
                }
                variant="outlined"
                sx={secondaryButtonSx}
              >
                Somente essenciais
              </Button>
              <Button
                onClick={() =>
                  persistPreferences({
                    essential: true,
                    analytics: true,
                    personalization: true,
                    marketing: true,
                  })
                }
                variant="contained"
                sx={primaryButtonSx}
              >
                Aceitar todos
              </Button>
            </Box>
          </Box>

          <Collapse in={showPreferences}>
            <Divider />

            <Box sx={{ p: { xs: 2.4, lg: 3 } }}>
              <Typography sx={{ color: "var(--oxy-text-primary)", mb: 2.5 }}>
                Você pode revisar suas escolhas a qualquer momento no rodapé ou
                na página de Privacidade e LGPD.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2.2,
                    borderRadius: 2.5,
                    border: "1px solid var(--oxy-border)",
                    bgcolor: "var(--oxy-surface-subtle)",
                  }}
                >
                  <Typography sx={{ color: "var(--oxy-text-primary)", mb: 0.8 }}>
                    Cookies essenciais
                  </Typography>
                  <Typography sx={{ color: "var(--oxy-text-secondary)", lineHeight: 1.7 }}>
                    Necessários para segurança, autenticação de sessão,
                    estabilidade e disponibilidade do portal.
                  </Typography>
                  <FormControlLabel
                    sx={{ mt: 1.5, ml: 0 }}
                    control={<Switch checked disabled />}
                    label="Sempre ativos"
                  />
                </Box>

                {preferenceOptions.map((option) => (
                  <Box
                    key={option.key}
                    sx={{
                      p: 2.2,
                      borderRadius: 2.5,
                      border: "1px solid var(--oxy-border)",
                      bgcolor: "var(--oxy-surface)",
                    }}
                  >
                    <Typography sx={{ color: "var(--oxy-text-primary)", mb: 0.8 }}>
                      {option.title}
                    </Typography>
                    <Typography sx={{ color: "var(--oxy-text-secondary)", lineHeight: 1.7 }}>
                      {option.description}
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 1.5, ml: 0 }}
                      control={
                        <Switch
                          checked={preferences[option.key]}
                          onChange={() => handleTogglePreference(option.key)}
                        />
                      }
                      label={
                        preferences[option.key] ? "Ativado" : "Desativado"
                      }
                    />
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  mt: 2.5,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  gap: 1.5,
                  alignItems: { sm: "center" },
                }}
              >
                <Typography sx={{ color: "var(--oxy-text-secondary)", fontSize: "0.9rem" }}>
                  {hasStoredPreferences && preferences.updatedAt
                    ? `Última atualização em ${new Date(
                        preferences.updatedAt,
                      ).toLocaleDateString("pt-BR")}.`
                    : "Nenhuma preferência opcional salva até o momento."}
                </Typography>

                <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap" }}>
                  {hasStoredPreferences ? (
                    <Button
                      onClick={() => setIsOpen(false)}
                      variant="text"
                      sx={closeButtonSx}
                    >
                      Fechar
                    </Button>
                  ) : null}
                  <Button
                    onClick={() =>
                      persistPreferences({
                        essential: true,
                        analytics: preferences.analytics,
                        personalization: preferences.personalization,
                        marketing: preferences.marketing,
                      })
                    }
                    variant="contained"
                    sx={primaryButtonSx}
                  >
                    Salvar preferências
                  </Button>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Container>
    </Box>
  );
}

const secondaryButtonSx = {
  borderColor: "var(--oxy-border-strong)",
  color: "var(--oxy-text-primary)",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: 2,
  px: 2.2,
  "&:hover": {
    borderColor: "var(--oxy-primary)",
    bgcolor: "var(--oxy-surface-subtle)",
  },
};

const primaryButtonSx = {
  bgcolor: "var(--oxy-primary)",
  color: "var(--oxy-surface)",
  textTransform: "none",
  fontWeight: 600,
  borderRadius: 2,
  px: 2.2,
  boxShadow: "none",
  "&:hover": {
    bgcolor: "var(--oxy-primary-dark)",
    boxShadow: "none",
  },
};

const closeButtonSx = {
  color: "var(--oxy-text-secondary)",
  textTransform: "none",
  "&:hover": {
    bgcolor: "transparent",
    color: "var(--oxy-text-primary)",
  },
};

const policyLinkButtonSx = {
  mt: 0.8,
  p: 0,
  minWidth: 0,
  color: "var(--oxy-primary)",
  textTransform: "none",
  justifyContent: "flex-start",
  "&:hover": {
    bgcolor: "transparent",
    color: "var(--oxy-primary-dark)",
  },
};
